
import { useState, useEffect } from 'react';
import { NewsCard } from "./NewsCard";

interface NewsItem {
  id: string;
  title: string;
  description: string | null;
  url: string;
  image_url: string | null;
  source: string;
  published_at: string;
  category?: string | null;
}

interface NewsGridProps {
  news: NewsItem[];
  isLoading?: boolean;
}

export function NewsGrid({ news, isLoading = false }: NewsGridProps) {
  // Add a fade-in animation to each news item
  const [visible, setVisible] = useState(false);
  
  useEffect(() => {
    // Small delay to make the animation more visible when new content loads
    const timer = setTimeout(() => {
      setVisible(true);
    }, 150);
    
    return () => {
      clearTimeout(timer);
      setVisible(false);
    };
  }, [news]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="bg-muted rounded-lg h-96" />
        ))}
      </div>
    );
  }

  if (news.length === 0) {
    return (
      <div className="text-center p-8 border rounded-lg bg-muted/30">
        <h3 className="text-lg font-medium">No news articles found</h3>
        <p className="text-muted-foreground mt-2">
          Try adjusting your filters or check back later for more travel news.
        </p>
      </div>
    );
  }

  // Validate news items before rendering
  const validNews = news.filter(item => 
    item.title && 
    item.url && 
    typeof item.url === 'string' && 
    item.url.trim() !== ''
  );

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-opacity duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}>
      {validNews.map((item) => (
        <NewsCard
          key={item.id}
          title={item.title}
          description={item.description}
          url={item.url}
          imageUrl={item.image_url}
          source={item.source}
          publishedAt={item.published_at}
        />
      ))}
    </div>
  );
}
